<script context="module">
  export const defaultSort = 'random'
</script>

<script>
  import router from 'page'
  import { products } from '../models/products.js'
  import { productTypes } from '../models/productTypes.js'
  import FormCheckbox from './FormCheckbox.svelte'

  export var admin = false
  export var open = true

  export var active
  export var productType
  export var sortBy = defaultSort
  export var checkedBoxes = []

  export var showSort = true
  export var showType = false
  export var showStatus = false

  export var keyword = ''

  function toggleOpen() {
    open = !open
  }

  function applyFilters(e) {
    // let user hit enter key to search
    if (e && e.key !== 'Enter') return

    // Collect all of the checked filters
    let query = []
    checkedBoxes = []
    let checkedFilters = document.querySelectorAll('input:checked')
    for (var i = 0, len = checkedFilters.length; i < len; i++) {
      let raw = checkedFilters[i].value
      let val = encodeURIComponent(raw)

      if (checkedFilters[i].name === 'category') {
        query.push(`category=${val}`)

        // The following checkedboxes string is hardcoded and used by external
        // packages to select and deselect filters. Please do not change string
        checkedBoxes.push(`category-${raw}`)
      } else {
        query.push(`attr-name=${checkedFilters[i].name}&attr-val=${val}`)

        // The following checkedboxes string is hardcoded and used by external
        // packages to select and deselect filters. Please do not change string
        checkedBoxes.push(`${checkedFilters[i].name}-${raw}`)
      }
    }

    // Keyword Search Term
    let searchInput = document.getElementById('keyword')

    // Update the URL
    updateUrl([...query], searchInput.value, sortBy)

    if (admin) {
      // Admin routes never parse filters back out of the URL, so query the
      // store directly
      products.setFilters({
        type: productType,
        sortBy: sortBy,
        active: active,
        query: query,
        search: searchInput.value,
        admin,
      })
    } else {
      // if mobile, hide filters component -- this function doesn't do anything if not mobile on the regular client
      toggleOpen();
    }
  }

  function updateUrl(params, search, sort) {
    // Update URL with new search params
    var url = window.location.pathname

    // Note that type and active are controlled by the URL
    if (sort !== defaultSort) {
      params.push('sort-by=' + sort)
    }
    if (search) {
      params.push('query=' + search)
    }

    // Join all query params and add the query string indicator
    if (params.length > 0) {
      url += '?' + params.join('&')
    }

    if (admin) {
      window.history.pushState({ path: url }, '', url)
    } else {
      // Navigate through the router so the history entry is one it can
      // replay on back/forward; Products.svelte reacts to the URL change by
      // re-parsing the filters and reloading the list
      router(url)
    }
  }

  function clearFilters() {
    // Reset checkboxes
    checkedBoxes = []
    let checkedFilters = document.querySelectorAll('input:checked')
    for (var i = 0, len = checkedFilters.length; i < len; i++) {
      checkedFilters[i].checked = false
    }

    // Reset sort
    sortBy = defaultSort

    // Clear search box
    let searchInput = document.getElementById('keyword')
    searchInput.value = null

    // Update the URL; for the non-admin client this navigates via the
    // router, and Products.svelte reloads the list from the URL
    updateUrl([], '', sortBy)

    if (admin) {
      // Reload products
      products.setFilters({
        type: productType,
        active: active,
        admin,
      })
    }
  }
</script>

<style type="text/less">
  .products-filters {
    display: flex;
    flex-direction: column;
    flex: 0 1 200px;
    position: absolute;
    top: 0;
    bottom: 0;
    padding: 0 var(--spacing-sm) var(--spacing-sm) 0;
    border-right: solid 1px var(--col-gray-80);
    background: var(--col-gray-95);
    transform: translateX(-100%);
    transition: transform 0.4s;
    z-index: 1;

    &.open {
      transform: translateX(0);
    }

    .title {
      display: flex;
      justify-content: space-between;
    }
  }

  .filter-option {
    display: flex;
    cursor: pointer;
    font-size: 0.9rem;
    align-items: center;

    &:hover {
      color: var(--col-link-hover);
    }

    input {
      margin-right: var(--spacing-xs);
    }
  }

  @media (min-width: 800px) {
    .products-filters {
      position: static;
      transform: translateX(0);
    }
    .filter-btn {
      display: none;
    }
  }

  .filter-group {
    max-height: 150px;
    overflow: auto;
  }
</style>

<div class="products-filters" class:open>
  <h3 class="title">
    Filters
    <button class="btn btn-link filter-btn" on:click={toggleOpen}>
      <svg class="icon">
        <use href="/icons.svg#clear" />
      </svg>
    </button>
  </h3>

  {#if showSort}
    <h4>Sort By</h4>
    <div class="products-sort">
      <select class="form-input form-input-sm" bind:value={sortBy}>
        <option value="random">Default</option>
        <option value="a-z">A to Z</option>
        <option value="z-a">Z to A</option>
        <option value="newest">Latest</option>
        <option value="oldest">Oldest</option>
        <option value="price-asc">Price (Low to High)</option>
        <option value="price-desc">Price (High to Low)</option>
      </select>
    </div>
  {/if}

  {#await $products}
    <div class="text-center">
      <svg class="icon spin">
        <use href="/icons.svg#circle_notch" />
      </svg>
      Loading Filters...
    </div>

  {:then $products}
    {#if admin}
      <hr />
      <div class="btn-group-expanded">
        <button class="btn btn-sm" on:click={() => applyFilters()}>
          Apply Filters
        </button>
        <button class="btn btn-sm" on:click={() => clearFilters()}>
          Clear Filters
        </button>
      </div>
    {/if}
    <div>
      <label for="keyword">
        <h4 class="form-input-label" for="keyword">Keyword</h4>
      </label>
      <input
        class="form-input form-input-sm"
        id="keyword"
        value={keyword}
        on:keydown={applyFilters} />
    </div>

    {#if showType}
      <h4>Product Type</h4>
      <div class="products-type">
        <select
          class="form-input form-input-sm"
          on:change={(e) => (productType = e.target.value)}>
          <option value={null}>Show All</option>
          {#each $productTypes as type}
            <option value={type.id}>{type.name}</option>
          {/each}
        </select>
      </div>
    {/if}

    {#if showStatus}
      <h4>Status</h4>
      <div class="products-status">
        <select
          class="form-input form-input-sm"
          on:change={(e) => (active = e.target.value)}>
          <option value={null}>Show All</option>
          <option value={0}>Private</option>
          <option value={1}>Public</option>
        </select>
      </div>
    {/if}

    {#if $products.filters.categories.length > 0}
      <h4>Categories</h4>
      <div class="filter-group">
        {#each $products.filters.categories as cat}
          <label class="form-field-label filter-option">
            <input
              type="checkbox"
              name="category"
              value={cat.category}
              checked={checkedBoxes.some((x) => x === 'category-' + cat.category)} />
            <span>{cat.category} ({cat.count})</span>
          </label>
        {/each}
      </div>
    {/if}

    {#each Object.keys($products.filters.attributes) as attr}
      <h4>{attr}</h4>
      <div class="filter-group">
        {#each $products.filters.attributes[attr] as value}
          <label class="form-field-label filter-option">
            <input
              type="checkbox"
              name={attr}
              value={value.value}
              checked={checkedBoxes.some((x) => x === attr + '-' + value.value)} />
            <span>{value.value} ({value.count})</span>
          </label>
        {/each}
      </div>
    {/each}
    <hr />

    <div class="btn-group-expanded">
      <button class="btn btn-sm" on:click={() => applyFilters()}>
        Apply Filters
      </button>
      <button class="btn btn-sm" on:click={() => clearFilters()}>
        Clear Filters
      </button>
    </div>
  {/await}
</div>
